import { FC, useMemo } from 'react'
import classNames from 'classnames'
import { Popover } from 'antd'
import { ExclamationOutlined } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { selectIsAuth } from '@store/profile.reducer'
import { jsonConvert } from '@/helpers/jsonConvert'
import { tiptapValueEmpty } from '@/helpers/new/tiptapValueEmpty'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import { formatCourseDuration } from '@/helpers/formatCourseDuration'
import { useGetSettings } from '../../tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import TipTapView from '../TipTapView/TipTapView'
import defaultClassIcon from '@/images/logo.svg'
import { normalizeTiptapValue } from '@/helpers/new/normalizeTiptapValue'
import useRoles from '@/hooks/useRoles'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import cn from './index.module.css'

const EMPLOYEE_ENROLLMENT_TOOLTIP =
  'Сотрудникам недоступны запись и покупка. Кнопка показана для ознакомления'

export interface CatalogClass {
  id: number
  title: string
  amount: number | null
  one_pay: boolean | number
  access_by_days?: boolean
  custom_payment_period?: number | null
  description?: string | object | null
  media_icon_id?: number | null
  mediaIcon?: MediaFileData | null
  duration_in_months?: number | string | null
  duration_unit?: 'month' | 'week' | 'day' | null
  old_price?: number | string | null
}

interface ClassCatalogCardProps {
  classValue: CatalogClass
  onEnroll?: (classId: number) => void
  preview?: boolean
  className?: string
}

const ClassCatalogCard: FC<ClassCatalogCardProps> = ({
  classValue,
  onEnroll,
  preview = false,
  className,
}) => {
  const mainLogo = useSelector((store: any) => store.settings.mainLogo)
  const isAuth = useSelector(selectIsAuth)
  const { isEmployee } = useRoles()
  const isEmployeeRestricted = isAuth && isEmployee
  const { checkSettings } = useGetSettings()
  const showDuration =
    preview || checkSettings('catalog_displayCourseDurationInCard')
  const showOldPrice = preview || checkSettings('catalog_showOldPrice')

  const getPrice = (price: number | string | null | undefined) => {
    if (!classValue.one_pay) return 'Бесплатно'

    const amount = Number(price ?? 0)
    if (classValue.access_by_days && classValue.custom_payment_period) {
      return `${amount} ₽ / ${classValue.custom_payment_period} дн.`
    }

    return `${amount} ₽ / месяц`
  }

  const getIcon = (): string => {
    const path = classValue.mediaIcon?.path ?? mainLogo ?? defaultClassIcon
    if (path === defaultClassIcon || path?.startsWith('blob')) return path
    return getCorrectFilePath(path)
  }

  const tiptapData = useMemo(
    () => normalizeTiptapValue(jsonConvert(classValue.description ?? '')),
    [classValue.description],
  )
  const durationText = formatCourseDuration(
    classValue.duration_in_months,
    classValue.duration_unit,
  )
  const normalizedOldPrice = Number(classValue.old_price)
  const oldPrice =
    classValue.old_price == null ||
    classValue.old_price === '' ||
    !Number.isFinite(normalizedOldPrice)
      ? ''
      : normalizedOldPrice.toString()
  const hasDescription = !tiptapValueEmpty(tiptapData)

  return (
    <div className={classNames(cn.card, className)}>
      <div className={cn.cardHeader}>
        <div className={cn.title}>{classValue.title}</div>
        {showDuration && durationText && (
          <div className={cn.duration}>{durationText}</div>
        )}
      </div>
      <div className={cn.content}>
        <div className={cn.pricing}>
          <div className={cn.price}>{getPrice(classValue.amount)}</div>
          {showOldPrice && classValue.one_pay && oldPrice && (
            <div className={cn.oldPrice}>{getPrice(oldPrice)}</div>
          )}
        </div>
        <img className={cn.icon} src={getIcon()} alt="" />
      </div>
      <div className={cn.actions}>
        {hasDescription && (
          <Popover
            overlayClassName={cn.popover}
            placement="bottom"
            title={classValue.title}
            content={<TipTapView value={tiptapData} />}
          >
            <button className={cn.moreButton}>Подробнее</button>
          </Popover>
        )}
        <TooltipWrapper
          title={
            isEmployeeRestricted ? EMPLOYEE_ENROLLMENT_TOOLTIP : undefined
          }
          placement="top"
        >
          <button
            className={cn.enrollButton}
            aria-disabled={isEmployeeRestricted}
            onClick={() => {
              if (!isEmployeeRestricted) onEnroll?.(classValue.id)
            }}
          >
            Записаться
            {isEmployeeRestricted && (
              <ExclamationOutlined
                className={cn.employeeHintIcon}
                aria-hidden="true"
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            )}
          </button>
        </TooltipWrapper>
      </div>
    </div>
  )
}

export default ClassCatalogCard
